import { useState, useCallback, useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import { Trans } from "react-i18next";
import { Link } from "wouter";
import type { TFunction } from "i18next";
import type { Stripe } from "@stripe/stripe-js";
import { Shield, Tag, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@workspace/ui/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { CarbonOffsetCallout } from "@/components/checkout/CarbonOffsetCallout";
import { SectionErrorBoundary } from "@/components/SectionErrorBoundary";
import InlinePaymentSection, { InlinePayButton, PaymentElementsWrapper } from "@/components/checkout/InlinePayment";
import type { PrepareBookingResult, PaymentErrorKind, WalletAnalyticsContext } from "@/components/checkout/InlinePayment";
import { PayOptionCard } from "@/components/checkout/CheckoutPrimitives";
import { resolvePayBlockedReason, revealPayBlocker, trackPayBlocked } from "@/components/checkout/pay-blocked";
import type { PriceQuote, SavedPaymentMethod, InsurancePackageWithPricing } from "@/lib/bookings-api";
import { REQUEST_OFFER_EXPIRATION_HOURS } from "@workspace/booking-rules";
import { depositCopyKey, type DepositCopyVariant } from "@/lib/checkout-deposit-copy";

type CancellationPolicy = "non-refundable" | "flexible";
type PaymentTiming = "pay_now" | "pay_later";

export function CheckoutPaymentSection({
  t,
  depositCopyVariant,
  isUsSelfDrive = false,
  hasPendingPayment = false,
  quote,
  isInstant,
  selectedCancellationPolicy,
  onSelectCancellationPolicy,
  fmtPrice,
  formatDate,
  nonRefundableTotal,
  refundableTotal,
  payLater,
  paymentTiming,
  setPaymentTiming,
  totalDue,
  stripePromise,
  stripeReady,
  savedCards,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  setPaymentMethodTouched,
  prepareBooking,
  precheckNote,
  handlePaymentSuccess,
  handlePaymentError,
  sym,
  cardBrandLabel,
  setCardBillingPostalCode,
  checkoutIsInsurable,
  loadingInsurance,
  insuranceLoadFailed = false,
  insurancePaymentBlocked,
  onRetryInsurance,
  selectedInsurancePackage,
  recommendedPackage,
  setInsuranceModalOpen,
  appliedPromoCode,
  handleRemovePromo,
  promoDialogOpen,
  setPromoDialogOpen,
  promoCode,
  setPromoCode,
  promoError,
  setPromoError,
  promoLoading,
  handleApplyPromo,
  climateOffsetFlagOn,
  climateOffsetEnabled,
  setClimateOffsetEnabled,
  setClimateModalOpen,
  deliveryRangeError,
  rentalEligibilityError,
  booking,
  phoneGateBlocking,
  updatingQuote,
  taxRecalcInFlight,
  quoteStalled = false,
  onRetryQuote,
  payNoteBlocking,
  payButtonLabel,
  setBookingStep,
  setBooking,
  pfcFlagLoading,
  carId,
  rentalType,
  customQuoteActive = false,
  checkoutJourneyId = "",
  driverChoiceBlockMessage = null,
  primaryDriverChoice,
  occasionBlockMessage,
  reserveFormError,
  onValidateForm,
}: {
  t: TFunction;
  depositCopyVariant?: DepositCopyVariant | null;
  isUsSelfDrive?: boolean;
  driverChoiceBlockMessage?: string | null;
  primaryDriverChoice?: boolean;
  occasionBlockMessage?: string | null;
  reserveFormError?: string | null;
  onValidateForm?: () => boolean;
  quote: PriceQuote;
  hasPendingPayment?: boolean;
  /** Task #5473 — true when this checkout confirms instantly: either an
   *  instant-book listing (`quote.instantBooking`) OR an active host quote
   *  (`quoteActive`). Must mirror the server's instant decision because it
   *  drives the Stripe Elements `capture_method`, which has to match the
   *  PaymentIntent's capture method or Stripe rejects the confirm. */
  isInstant: boolean;
  selectedCancellationPolicy: CancellationPolicy;
  onSelectCancellationPolicy: (policy: CancellationPolicy) => void;
  fmtPrice: (n: number) => string;
  formatDate: (dateStr: string | null | undefined, options?: Intl.DateTimeFormatOptions, countryCode?: string | null) => string;
  nonRefundableTotal: number;
  refundableTotal: number;
  payLater: PriceQuote["payLater"];
  paymentTiming: PaymentTiming;
  setPaymentTiming: Dispatch<SetStateAction<PaymentTiming>>;
  totalDue: number;
  stripePromise: Promise<Stripe | null> | null;
  stripeReady: boolean;
  savedCards: SavedPaymentMethod[];
  selectedPaymentMethod: string;
  setSelectedPaymentMethod: Dispatch<SetStateAction<string>>;
  setPaymentMethodTouched: Dispatch<SetStateAction<boolean>>;
  prepareBooking: (paymentMethodId?: string) => Promise<PrepareBookingResult>;
  precheckNote?: () => Promise<boolean>;
  handlePaymentSuccess: (publicId: string) => void;
  handlePaymentError: (msg: string, kind?: PaymentErrorKind) => void;
  sym: string;
  cardBrandLabel: (brand: string) => string;
  setCardBillingPostalCode: (postalCode: string) => void;
  checkoutIsInsurable: boolean;
  loadingInsurance: boolean;
  insuranceLoadFailed?: boolean;
  /** Coverage confirmation, independent of loading optional alternatives. */
  insurancePaymentBlocked?: boolean;
  onRetryInsurance?: () => void;
  selectedInsurancePackage: Pick<InsurancePackageWithPricing, "id" | "name" | "dailyRate" | "policy" | "dailyBreakdown"> | null;
  recommendedPackage: InsurancePackageWithPricing | null;
  setInsuranceModalOpen: Dispatch<SetStateAction<boolean>>;
  appliedPromoCode: string;
  handleRemovePromo: () => void;
  promoDialogOpen: boolean;
  setPromoDialogOpen: Dispatch<SetStateAction<boolean>>;
  promoCode: string;
  setPromoCode: Dispatch<SetStateAction<string>>;
  promoError: string;
  setPromoError: Dispatch<SetStateAction<string>>;
  promoLoading: boolean;
  handleApplyPromo: () => void;
  climateOffsetFlagOn: boolean;
  climateOffsetEnabled: boolean;
  setClimateOffsetEnabled: Dispatch<SetStateAction<boolean>>;
  setClimateModalOpen: Dispatch<SetStateAction<boolean>>;
  deliveryRangeError: string | null;
  rentalEligibilityError?: string | null;
  booking: boolean;
  phoneGateBlocking: boolean;
  updatingQuote: boolean;
  taxRecalcInFlight: boolean;
  /** Task #8483 — true when the last price refresh hit the client-side
   *  stall watchdog instead of settling normally. Unlike `updatingQuote`/
   *  `taxRecalcInFlight` (transient — button just stays disabled, no
   *  reason tracked), this is user-actionable: Pay stays disabled with
   *  explicit helper text and `onRetryQuote` re-fires the fetch. */
  quoteStalled?: boolean;
  onRetryQuote?: () => void;
  payNoteBlocking?: boolean;
  payButtonLabel: string;
  setBookingStep: Dispatch<SetStateAction<"form" | "confirming">>;
  /** C1 — setter for the parent page's `booking` in-flight flag. Called with
   *  `true` when EITHER the desktop or mobile pay button starts processing,
   *  and `false` when it finishes. Ensures both buttons are disabled once
   *  one of them has started so a second tap from the other variant can't
   *  launch a concurrent booking request. Optional only for backwards compat
   *  with test renders that don't wire the page-level flag. */
  setBooking?: Dispatch<SetStateAction<boolean>>;
  /** Task #5038 — true while the feature-flags fetch is still in flight. The
   *  Pay button stays disabled until flags resolve so prepareBooking can never
   *  run with a stale `false` payment-first-checkout flag (which would silently
   *  fall back to the legacy create-trip-then-charge path). */
  pfcFlagLoading?: boolean;
  /** Task #6144 — analytics context for the checkout_pay_blocked events.
   *  Optional: test renders and legacy call sites may omit them. */
  carId?: number;
  rentalType?: string;
  /** Active host-generated custom quote. This only shapes safe wallet
   * observability; pricing and policy remain server-authoritative. */
  customQuoteActive?: boolean;
  checkoutJourneyId?: string;
}) {
  // Task #3499: Stripe Elements must be initialized in the same currency
  // the server denominated the PaymentIntent in (the CHARGE currency),
  // not the car's local listing currency. For full-pay markets the two
  // are identical (chargeCurrency === currency, fxRate === 1) so this is
  // a no-op; for partial-pay markets (e.g. NZ, which captures in USD)
  // the local currency (NZD) differs from the charge currency (USD) and
  // initializing Elements in NZD made stripe.confirmPayment fail with
  // "The provided currency (usd) does not match the expected currency
  // (nzd)". We source the Elements currency from `quote.chargeCurrency`
  // (falling back to `quote.currency` only when absent) with NO silent
  // "USD" default — if neither resolves we block the pay button below.
  const chargeCurrency = (quote.chargeCurrency || quote.currency || "").trim();
  const chargeCurrencyMissing = chargeCurrency.length === 0;
  // The amount handed to Elements (and therefore the Express Checkout /
  // Apple Pay / Google Pay sheet) must be expressed in the charge
  // currency too, otherwise the wallet shows the local figure against
  // the charge currency. Convert the local amount at the snapshotted
  // `quote.fxRate` (1.0 for full-pay markets, so identical to today).
  const fxRate =
    Number.isFinite(Number(quote.fxRate)) && Number(quote.fxRate) > 0
      ? Number(quote.fxRate)
      : 1;
  const toChargeAmount = (local: number) =>
    Math.round((Number(local) || 0) * fxRate * 100) / 100;
  const localPayAmount =
    paymentTiming === "pay_later"
      ? 0
      : totalDue;
  const elementsAmount = toChargeAmount(localPayAmount);
  // When the charge currency can't be resolved we must not mount Elements
  // (it would fall back to a guessed currency) — gate Stripe off and keep
  // the pay button disabled with a retry helper instead of confirming
  // against a fabricated currency.
  const stripeUsable = stripeReady && !chargeCurrencyMissing;

  // Stripe Elements must be remounted after any failed payment attempt.
  // After elements.submit() runs, the Stripe deferred-intent state is
  // "locked"; if confirmPayment never runs (e.g. server moderation block or
  // card error) a subsequent elements.submit() produces a stale PM and
  // confirmPayment fails with "Invalid payment method". Bumping this key
  // force-remounts <Elements> so each retry starts from a clean state.
  const [elementsResetKey, setElementsResetKey] = useState(0);
  const walletCorrelationRef = useRef<string | null>(checkoutJourneyId || null);
  if (!walletCorrelationRef.current) walletCorrelationRef.current = `wallet-${Math.random().toString(36).slice(2, 14)}`;
  const walletAnalyticsContext: WalletAnalyticsContext = {
    correlation_id: walletCorrelationRef.current,
    custom_quote_active: customQuoteActive,
    cancellation_policy: selectedCancellationPolicy,
    quote_state: updatingQuote ? "refreshing" : "settled",
    rental_type: rentalType,
    payment_timing: paymentTiming,
  };
  const resetElements = useCallback(() => setElementsResetKey(k => k + 1), []);

  // Task #4687 — when a moderation block remounts the Elements, the renter's
  // typed card is wiped. Surface a calm "we cleared your card for security,
  // re-enter it" notice so the cleared card field is explained rather than
  // appearing as a mysterious failure on the next Pay click. Dismissed when the
  // renter starts re-entering their card (onCardFieldChange) or on a payment
  // error/success (the next attempt's outcome supersedes it).
  const [cardResetNotice, setCardResetNotice] = useState(false);

  // Remount Elements AND raise the calm re-enter notice. Wired to every
  // onElementsReset call site (card Pay button + wallet flows) so a block on
  // any path explains the wiped card identically.
  const onModerationBlockReset = useCallback(() => {
    resetElements();
    setCardResetNotice(true);
  }, [resetElements]);

  const handlePaymentErrorWithReset = useCallback((msg: string, kind?: PaymentErrorKind) => {
    resetElements();
    // The payment-error banner now owns the messaging; clear the standalone
    // card-reset notice so the two don't stack.
    setCardResetNotice(false);
    handlePaymentError(msg, kind);
  }, [resetElements, handlePaymentError]);

  // Dismiss the card-reset notice the moment the renter re-enters card details.
  const handleCardFieldChange = useCallback((hasInput: boolean) => {
    if (hasInput) setCardResetNotice(false);
  }, []);

  // Task #6144 — resolve the user-actionable gate (if any) currently
  // blocking the pay button. Transient gates (booking, updatingQuote,
  // taxRecalcInFlight, pfcFlagLoading) never resolve to a reason — the
  // button stays disabled and taps remain inert for those.
  const payBlockedReason = resolvePayBlockedReason({
    chargeCurrencyMissing,
    quoteStalled,
    phoneGateBlocking,
    payNoteBlocking,
    deliveryRangeError,
    driverChoiceBlockMessage,
    primaryDriverChoice,
    occasionRequired: !!occasionBlockMessage,
  });
  const coverageBlocked = insurancePaymentBlocked ?? (checkoutIsInsurable && (loadingInsurance || insuranceLoadFailed || !selectedInsurancePackage));
  const safetyDisabled = hasPendingPayment || !stripeUsable || booking || !!deliveryRangeError || !!rentalEligibilityError || phoneGateBlocking
    || updatingQuote || taxRecalcInFlight || !!quoteStalled || coverageBlocked
    || !!payNoteBlocking || chargeCurrencyMissing || !!pfcFlagLoading;

  // Fire checkout_pay_blocked once per reason per checkout view (the ref
  // set persists across re-renders but resets on a fresh page mount).
  const firedBlockedReasonsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!payBlockedReason) return;
    if (firedBlockedReasonsRef.current.has(payBlockedReason)) return;
    firedBlockedReasonsRef.current.add(payBlockedReason);
    trackPayBlocked("checkout_pay_blocked", {
      ...(checkoutJourneyId ? { checkout_journey_id: checkoutJourneyId } : {}),
      reason: payBlockedReason,
      car_id: carId,
      rental_type: rentalType,
    });
  }, [payBlockedReason, carId, rentalType]);

  // Tap on the disabled pay button → capture + scroll to/highlight the
  // blocking section. Passed to the buttons only when a reason exists.
  const handleBlockedPayTap = useCallback(() => {
    if (!payBlockedReason) return;
    trackPayBlocked("checkout_pay_blocked_tap", {
      ...(checkoutJourneyId ? { checkout_journey_id: checkoutJourneyId } : {}),
      reason: payBlockedReason,
      car_id: carId,
      rental_type: rentalType,
    });
    revealPayBlocker(payBlockedReason);
  }, [payBlockedReason, carId, rentalType]);
  const onDisabledPayTap = payBlockedReason && !booking && !updatingQuote && !taxRecalcInFlight && !pfcFlagLoading
    && !coverageBlocked && stripeReady
    ? handleBlockedPayTap : undefined;

  // Cancellation policy is locked to non-refundable when the market is
  // partial-payment-only — partial-pay bookings hide the refundable upgrade
  // and pin the non-refundable card. Host custom quotes (Task #4602) keep the
  // normal refundable/non-refundable choice, exactly like a listing quote.
  const nonRefundableOnly = quote.paymentType === "partial";

  return (
    <>
      {/* 2. Cancellation policy */}
      <section className="rounded-xl border border-border p-5">
        <h2 className="text-base font-semibold text-foreground">{t("checkout.cancellation_title")}</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {quote.paymentType === "partial"
            ? t("checkout.cancellation_subtitle_partial", { defaultValue: "Partial-payment bookings are non-refundable." })
            : t("checkout.cancellation_subtitle")}
        </p>
        <div className="mt-4 space-y-3">
          <PayOptionCard
            selected={selectedCancellationPolicy === "non-refundable"}
            onSelect={nonRefundableOnly ? () => {} : () => onSelectCancellationPolicy("non-refundable")}
            title={t("checkout.non_refundable_option_with_total", {
              amount: `${fmtPrice(nonRefundableTotal)}`,
              defaultValue: `Non-refundable · ${fmtPrice(nonRefundableTotal)}`,
            })}
            description={quote.paymentType === "partial"
              ? t("checkout.non_refundable_partial_desc", { defaultValue: "No refund if you cancel. Platform fees are non-refundable on partial-payment bookings." })
              : t("checkout.non_refundable_desc")}
          />
          {!nonRefundableOnly && (
            <PayOptionCard
              selected={selectedCancellationPolicy === "flexible"}
              onSelect={() => onSelectCancellationPolicy("flexible")}
              title={t("checkout.refundable_option", {
                amount: `${fmtPrice(refundableTotal)}`,
              })}
              description={t("checkout.refundable_desc")}
            />
          )}
        </div>
      </section>

      {/* 3. Choose when to pay — strictly derived from the server-returned
          quote. Pay-later copy (amounts, dates) is read from the same
          payload so this UI stays in lockstep with backend eligibility.
          Pay-later is additionally gated on the Refundable
          policy — on non-refundable bookings the host needs the money
          up-front, mirroring the server's offer logic. */}
      {(() => {
        // The US PFC endpoint rejects pay_later even if an older quote offers it.
        const payLaterAvailable = !isUsSelfDrive && payLater.available && selectedCancellationPolicy === "flexible";
        if (!payLaterAvailable) return null;

        type PlanOption = {
          key: string;
          selected: boolean;
          onSelect: () => void;
          title: string;
          description: string;
        };
        const plans: PlanOption[] = [
          {
            key: "pay_now_full",
            selected: paymentTiming === "pay_now",
            onSelect: () => setPaymentTiming("pay_now"),
            title: t("checkout.pay_full_now_title", { amount: `${fmtPrice(totalDue)}` }),
            description: t("checkout.pay_full_now_desc"),
          },
        ];
        if (payLaterAvailable) {
          plans.push({
            key: "pay_later",
            selected: paymentTiming === "pay_later",
            onSelect: () => setPaymentTiming("pay_later"),
            title: t("checkout.pay_later_title"),
            description: (payLater.chargeDate
              ? t("checkout.pay_later_desc", {
                  amount: `${fmtPrice(payLater.chargeAmount)}`,
                  when: formatDate(payLater.chargeDate, { month: "long", day: "numeric" }),
                })
              : t("checkout.pay_later_desc_pickup", {
                  amount: `${fmtPrice(payLater.chargeAmount)}`,
                })) + (quote.deposit > 0 ? ` ${t(depositCopyKey(depositCopyVariant ?? "generic", "note"))}` : ""),
          });
        }
        return (
          <section className="rounded-xl border border-border p-5">
            <h2 className="text-base font-semibold text-foreground">{t("checkout.choose_when_to_pay")}</h2>
            <div className="mt-4 space-y-3">
              {plans.map((p) => (
                <PayOptionCard
                  key={p.key}
                  selected={p.selected}
                  onSelect={p.onSelect}
                  title={p.title}
                  description={p.description}
                />
              ))}
            </div>
          </section>
        );
      })()}

      {/* 3. Payment + everything below it that needs Stripe Elements is wrapped
            in a single <Elements> context (deferred intent mode). PaymentElement
            + ExpressCheckout mount immediately; the booking + intent are created
            on submit by prepareBooking, avoiding orphan trips. The Pay button
            lives inside the same wrapper so useStripe / useElements resolve.
            When the server hasn't returned a publishable key (test envs,
            Stripe-disabled regions), the wrapper renders children without
            an Elements context — InlinePayButton stays disabled, the
            inline section skips its Stripe-only sub-trees. */}
      {/* Task #1709: the standalone "Billing ZIP for sales tax" section
          was removed here. It duplicated the postcode the Stripe card
          form collects below and broke for non-US renters. The sales-tax
          line in the price breakdown now starts from the pickup-location
          estimate and is sharpened from the PaymentElement's billing
          postal code via `onBillingPostalCodeChange` → the
          cardBillingPostalCode debounce effect above. */}
      <PaymentElementsWrapper
        stripePromise={stripePromise}
        amount={elementsAmount}
        currency={chargeCurrency}
        mode={paymentTiming === "pay_later" ? "setup" : "payment"}
        // The quote is the authoritative preflight for the Stripe capture
        // contract. In particular, an instant self-drive booking awaiting
        // licence verification must stay manual even though its CTA is
        // instant. Elements is mounted before submit, so do not wait for the
        // post-submit PaymentIntent response to configure this.
        captureMethod={quote.expectedCaptureMethod ?? (isInstant ? "automatic_async" : "manual")}
        resetKey={elementsResetKey}
      >
        {cardResetNotice && (
          <Alert
            variant="info"
            data-testid="checkout-card-reset-notice"
            className="mb-3"
          >
            <AlertDescription>{t("checkout.card_reset_notice")}</AlertDescription>
          </Alert>
        )}
        <InlinePaymentSection
          stripeAvailable={stripeUsable}
          driverChoiceBlockMessage={driverChoiceBlockMessage}
          formBlocked={!!driverChoiceBlockMessage || !!occasionBlockMessage}
          amount={elementsAmount}
          mode={paymentTiming === "pay_later" ? "setup" : "payment"}
          savedCards={savedCards.map(c => ({
            id: c.id,
            brand: c.brand,
            last4: c.last4,
            expMonth: c.expMonth,
            expYear: c.expYear,
            isDefault: c.isDefault,
          }))}
          selectedPaymentMethod={selectedPaymentMethod}
          onSelectPaymentMethod={(id) => { setSelectedPaymentMethod(id); setPaymentMethodTouched(true); }}
          prepareBooking={prepareBooking}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentErrorWithReset}
          precheckNote={precheckNote}
          onElementsReset={onModerationBlockReset}
          onProcessingChange={(p) => { setBooking?.(p); if (p) setBookingStep("confirming"); }}
          displayedTotal={totalDue}
          deposit={paymentTiming === "pay_later" ? 0 : quote.deposit}
          depositCopyVariant={depositCopyVariant}
          currencySymbol={sym}
          cardBrandLabel={cardBrandLabel}
          onBillingPostalCodeChange={setCardBillingPostalCode}
          onCardFieldChange={handleCardFieldChange}
          pfcFlagLoading={pfcFlagLoading}
          payButtonDisabled={safetyDisabled || !!driverChoiceBlockMessage || !!occasionBlockMessage}
              walletAnalyticsContext={walletAnalyticsContext}
          paymentAnalyticsContext={{ checkout_journey_id: checkoutJourneyId }}
        />
        {/* Task #6588 — request-to-book hold explainer, placed just below the
            Secure payment box. The 48h window is sourced from the same
            REQUEST_OFFER_EXPIRATION_HOURS constant the api-server enforces
            (lib/booking-rules), so copy and expiry can't drift. Hidden for
            instant bookings (charged immediately) and pay-later (setup-mode:
            no authorisation hold is placed at confirm time). */}
        {!isInstant && paymentTiming !== "pay_later" && (
          <Alert
            variant="info"
            data-testid="checkout-request-hold-notice"
            className="mt-3"
          >
            <AlertDescription>
              {t("checkout.request_hold_notice", { hours: REQUEST_OFFER_EXPIRATION_HOURS })}
            </AlertDescription>
          </Alert>
        )}

        {/* 4. Insurance — essential for every self-drive trip */}
        {checkoutIsInsurable && (
          <section className="rounded-xl border border-border p-5">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-foreground">{t("checkout.self_drive_insurance")}</h2>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("checkout.insurance_section_help")}
            </p>
            {coverageBlocked && (
              <div className="mt-3 flex items-center justify-between gap-3" role="alert">
                <p className="text-sm text-destructive">{t("checkout.insurance_coverage_required")}</p>
                <button type="button" data-testid="checkout-required-coverage-change"
                  onClick={() => setInsuranceModalOpen(true)}
                  className="shrink-0 text-sm font-medium underline">
                  {t("common.change")}
                </button>
              </div>
            )}
            {loadingInsurance && !selectedInsurancePackage ? (
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner size="sm" /> {t("checkout.loading_packages")}
              </div>
            ) : insuranceLoadFailed && !selectedInsurancePackage ? (
              <div className="mt-4 flex items-center justify-between gap-4 rounded-lg border border-border bg-muted/30 p-3">
                <p className="text-sm text-muted-foreground">{t("insurance.load_error")}</p>
                <button
                  type="button"
                  data-testid="checkout-insurance-retry"
                  onClick={onRetryInsurance}
                  className="shrink-0 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted"
                >
                  {t("common.try_again")}
                </button>
              </div>
            ) : (
              <div className="mt-3 flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Shield className="mt-0.5 h-5 w-5 text-brand" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {selectedInsurancePackage
                        ? t("checkout.insurance_selected", { name: selectedInsurancePackage.name, rate: `${fmtPrice(selectedInsurancePackage.dailyRate)}` })
                        : recommendedPackage
                          ? t("checkout.insurance_recommended", { name: recommendedPackage.name, rate: `${fmtPrice(recommendedPackage.dailyRate)}` })
                          : t("checkout.insurance_coverage_required")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("checkout.insurance_tap_change")}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setInsuranceModalOpen(true)}
                  className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted"
                >
                  {t("common.change")}
                </button>
              </div>
            )}
            {insuranceLoadFailed && selectedInsurancePackage && (
              <button
                type="button"
                data-testid="checkout-insurance-retry"
                onClick={onRetryInsurance}
                className="mt-3 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted"
              >
                {t("common.try_again")}
              </button>
            )}
            {/* Task #2264 — when the renter is on a NY-located US self-drive
                car the only available policy is the Crum NY policy. Render
                the limits, deductible, daily split and stated-value cap
                inline so the disclosure is visible without opening the
                picker modal. */}
            {(selectedInsurancePackage?.policy || recommendedPackage?.policy) && (() => {
              const policy = (selectedInsurancePackage?.policy || recommendedPackage?.policy)!;
              const breakdown = (selectedInsurancePackage?.dailyBreakdown || recommendedPackage?.dailyBreakdown) || null;
              const fmt = (n: number) => `${fmtPrice(n)}`;
              return (
                <div className="mt-4 rounded-lg border border-border bg-muted/30 p-4 text-xs leading-snug text-foreground">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("checkout.coverage_limits_heading", { name: policy.name })}
                  </p>
                  <ul className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
                    <li>{t("checkout.coverage_liability", { value: (policy.limits.liability_csl / 1_000_000).toFixed(2) })}</li>
                    <li>{t("checkout.coverage_um_uim", { value: (policy.limits.um_uim / 1_000_000).toFixed(2) })}</li>
                    <li>{t("checkout.coverage_pip", { value: policy.limits.pip.toLocaleString() })}</li>
                    <li>{t("checkout.coverage_apd_cap", { value: policy.limits.apd_cap.toLocaleString() })}</li>
                    <li>{t("checkout.coverage_apd_deductible", { value: policy.deductible.toLocaleString() })}</li>
                    <li>{t("checkout.insurance_daily_total", { value: fmt(policy.daily_total) })}</li>
                  </ul>
                  {breakdown && (
                    <p className="mt-2 text-muted-foreground">
                      {t("checkout.coverage_per_day_split", { liability: fmt(breakdown.liability), apd: fmt(breakdown.apd) })}
                    </p>
                  )}
                  {policy.stated_value_capped && (
                    <p className="mt-2 text-amber-700">
                      {t("checkout.coverage_stated_value_capped", { value: policy.limits.apd_cap.toLocaleString() })}
                    </p>
                  )}
                </div>
              );
            })()}
          </section>
        )}

        {/* Driver verification (licence / age / Didit / Samba) is collected
              post-booking on the confirmation and trip pages for self-drive
              rentals. Chauffeur and event rentals require no licence at
              all, so checkout no longer renders an inline gate. */}

        {/*
          Promo code: collapsed by default behind a "Got a promo code?"
          link that opens a dialog. When a code is successfully applied,
          the inline success chip replaces the link so the renter sees
          confirmation without re-opening the dialog. The actual input +
          Apply button live in the <Dialog> below the section.
        */}
        {appliedPromoCode ? (
          <section className="rounded-xl border border-border p-5">
            <h2 className="text-base font-semibold text-foreground">{t("checkout.promo_code_title")}</h2>
            <Alert variant="success" icon={Tag} className="mt-3">
              <AlertDescription className="flex items-center justify-between">
                <span className="text-sm font-medium">{appliedPromoCode}</span>
                <button onClick={handleRemovePromo} className="rounded p-0.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/50">
                  <X className="h-3.5 w-3.5" />
                </button>
              </AlertDescription>
            </Alert>
          </section>
        ) : (
          <button
            type="button"
            onClick={() => { setPromoError(""); setPromoDialogOpen(true); }}
            className="inline-flex items-center gap-1.5 self-start rounded-md text-sm font-medium text-primary underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Tag className="h-3.5 w-3.5" aria-hidden="true" />
            {t("checkout.promo_have_question")}
          </button>
        )}

        <Dialog open={promoDialogOpen} onOpenChange={(open) => {
          setPromoDialogOpen(open);
          if (!open) { setPromoError(""); setPromoCode(""); }
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t("checkout.promo_code_title")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value); setPromoError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleApplyPromo(); }}
                  placeholder={t("checkout.promo_code_placeholder")}
                  autoFocus
                  className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm"
                />
                <button
                  onClick={handleApplyPromo}
                  disabled={promoLoading || !promoCode.trim()}
                  className="cursor-pointer rounded-md bg-accent px-4 py-2 text-xs font-semibold text-accent-foreground hover:bg-accent/90 disabled:opacity-50"
                >
                  {promoLoading ? <Spinner size="sm" /> : t("checkout.promo_apply")}
                </button>
              </div>
              {promoError && <p className="text-xs text-destructive">{promoError}</p>}
            </div>
          </DialogContent>
        </Dialog>

        {/* Carbon offset — surfaced before the booking CTA so renters can opt-in */}
        {climateOffsetFlagOn && quote.climateOffsetAmount > 0 && (
          <CarbonOffsetCallout
            amount={quote.climateOffsetAmount}
            currencySymbol={sym}
            enabled={climateOffsetEnabled}
            onToggle={() => setClimateOffsetEnabled((v) => !v)}
            onLearnMore={() => setClimateModalOpen(true)}
          />
        )}

        {/* Booking terms + MIT-consent clickwrap notice + Pay CTA.
            Task #6837 — always-visible (no checkbox) notice whose copy adapts
            to the selected payment plan and mentions the security deposit ONLY
            when one applies. Rendering it records the renter's Terms + MIT
            authorization; the hook sends `checkout_terms_accepted: true`. */}
        <p id="checkout-clickwrap-notice" className="px-1 text-xs text-muted-foreground">
          <Trans
            t={t}
            i18nKey={`checkout.clickwrap.${
              paymentTiming === "pay_later"
                ? "pay_later"
                : quote.paymentType === "partial"
                  ? "partial"
                  : "full"
            }`}
            components={{
              terms: (
                <Link
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                />
              ),
            }}
          />
          {quote.deposit > 0
            ? " " +
              t("checkout.clickwrap.deposit_suffix", {
                amount: `${sym}${quote.deposit.toLocaleString()}`,
              })
            : ""}
        </p>

        {deliveryRangeError && (
          <div id="checkout-delivery-range-error" role="alert" className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            {deliveryRangeError}
          </div>
        )}
        {rentalEligibilityError && (
          <div role="alert" data-testid="checkout-rental-eligibility-error" className="rounded-xl border border-amber-300/60 bg-amber-50/60 p-4 text-sm text-amber-900">
            {rentalEligibilityError}
          </div>
        )}

        {/* Task #8483 — a stalled price/tax refresh (client-side stall
            watchdog) keeps the last-known-good total on screen but must
            block payment on it (a partial-pay / FX / custom-quote total is
            too easy to under/over-charge if it's silently stale). Placed
            right above the pay button — the same spot the mobile sticky
            bar's blocked tap and the desktop currency-unresolved case
            already scroll to — so both button variants and both device
            widths show the retry affordance without needing the (desktop
            aside / mobile expandable) price panel open. */}
        {quoteStalled && !deliveryRangeError && (
          <div
            role="alert"
            className="flex items-center justify-between gap-3 rounded-xl border border-amber-300/60 bg-amber-50/60 p-4 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200"
          >
            <span>{t("checkout.quote_stalled_desc", "We couldn't load pricing for this trip in time. Your connection or our pricing service may be having a moment.")}</span>
            {onRetryQuote && (
              <button
                type="button"
                onClick={onRetryQuote}
                data-testid="checkout-pay-blocked-quote-retry"
                className="shrink-0 rounded-md border border-amber-400/60 bg-background px-3 py-1.5 font-semibold hover:bg-muted"
              >
                {t("checkout.try_again", "Try again")}
              </button>
            )}
          </div>
        )}

        <SectionErrorBoundary name="InlinePayButton">
          <div id="checkout-pay-button-anchor">
            <InlinePayButton
              variant="desktop"
              stripeAvailable={stripeUsable}
              stripePublishableKey={quote.stripePublishableKey}
              stripeRegion={quote.stripeRegion}
              mode={paymentTiming === "pay_later" ? "setup" : "payment"}
              savedCards={savedCards.map(c => ({ id: c.id, brand: c.brand, last4: c.last4, expMonth: c.expMonth, expYear: c.expYear, isDefault: c.isDefault }))}
              selectedPaymentMethod={selectedPaymentMethod}
              prepareBooking={prepareBooking}
              precheckNote={precheckNote}
              onValidateForm={onValidateForm}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentErrorWithReset}
              onElementsReset={onModerationBlockReset}
              onProcessingChange={(p) => { setBooking?.(p); if (p) setBookingStep("confirming"); }}
              displayedTotal={totalDue}
              payButtonLabel={payButtonLabel}
              onDisabledTap={onDisabledPayTap}
              transientLoading={updatingQuote || taxRecalcInFlight}
              payButtonDisabled={safetyDisabled}
              payButtonHelperText={chargeCurrencyMissing
                ? t("checkout.payment_currency_unresolved", "We couldn't prepare your payment. Please refresh and try again.")
                : quoteStalled
                  ? t("checkout.quote_stalled_pay_helper", "We couldn't refresh your price. Retry above to continue.")
                  : phoneGateBlocking
                    ? t("checkout.phone_verification_pay_helper", "Verify your phone number above to continue.")
                    : payNoteBlocking
                      ? t("checkout.message_to_host_pay_helper", "Update your message to the host to continue.")
                      : deliveryRangeError
                        ? deliveryRangeError
                        : rentalEligibilityError
                          ? rentalEligibilityError
                      : pfcFlagLoading
                        ? t("checkout.payment_options_loading", "Preparing secure checkout…")
                        : (updatingQuote || taxRecalcInFlight)
                          ? t("checkout.updating_price_helper", "Updating price, please wait…")
                          : coverageBlocked
                            ? t("checkout.insurance_coverage_required")
                            : null}
            />
            {/* Mobile-only inline Pay button — shares the Elements context. The
                sticky bottom bar's CTA scrolls renters here on mobile. */}
            <div className="md:hidden mt-2">
              <InlinePayButton
                variant="mobile-block"
                stripeAvailable={stripeUsable}
                stripePublishableKey={quote.stripePublishableKey}
                stripeRegion={quote.stripeRegion}
                mode={paymentTiming === "pay_later" ? "setup" : "payment"}
                savedCards={savedCards.map(c => ({ id: c.id, brand: c.brand, last4: c.last4, expMonth: c.expMonth, expYear: c.expYear, isDefault: c.isDefault }))}
                selectedPaymentMethod={selectedPaymentMethod}
                prepareBooking={prepareBooking}
                precheckNote={precheckNote}
                onValidateForm={onValidateForm}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentErrorWithReset}
                onElementsReset={onModerationBlockReset}
                onProcessingChange={(p) => { setBooking?.(p); if (p) setBookingStep("confirming"); }}
                displayedTotal={totalDue}
                payButtonLabel={payButtonLabel}
                onDisabledTap={onDisabledPayTap}
                transientLoading={updatingQuote || taxRecalcInFlight}
                payButtonDisabled={safetyDisabled}
                payButtonHelperText={chargeCurrencyMissing
                  ? t("checkout.payment_currency_unresolved", "We couldn't prepare your payment. Please refresh and try again.")
                  : quoteStalled
                    ? t("checkout.quote_stalled_pay_helper", "We couldn't refresh your price. Retry above to continue.")
                    : phoneGateBlocking
                      ? t("checkout.phone_verification_pay_helper", "Verify your phone number above to continue.")
                      : payNoteBlocking
                        ? t("checkout.message_to_host_pay_helper", "Update your message to the host to continue.")
                        : deliveryRangeError
                          ? deliveryRangeError
                          : rentalEligibilityError
                            ? rentalEligibilityError
                        : pfcFlagLoading
                          ? t("checkout.payment_options_loading", "Preparing secure checkout…")
                          : (updatingQuote || taxRecalcInFlight)
                            ? t("checkout.updating_price_helper", "Updating price, please wait…")
                            : null}
              />
            </div>
            {reserveFormError && !safetyDisabled && (
              <p role="alert" data-testid="checkout-reserve-validation" className="mt-2 text-sm font-medium text-destructive">
                {reserveFormError}
              </p>
            )}
          </div>
        </SectionErrorBoundary>
      </PaymentElementsWrapper>
    </>
  );
}
